import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useToken } from "@/lib/session";
import { Clock, Lock, ArrowLeft } from "lucide-react";

const PING = 30; // seconds between server heartbeats

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

// Wraps a game page: tracks daily play-time via heartbeats and blocks play
// once the admin-configured daily limit is reached (resets midnight IST).
export default function PlaytimeGuard({ children }) {
  const token = useToken();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [localRemaining, setLocalRemaining] = useState(null);
  const startedRef = useRef(Date.now());

  // initial status
  useEffect(() => {
    let on = true;
    api.get(`/employee/playtime?token=${token}`)
      .then((r) => { if (on) { setStatus(r.data); setLocalRemaining(r.data.remaining_seconds); } })
      .catch(() => { if (on) setStatus({ expired: false, unlimited: true }); });
    return () => { on = false; };
  }, [token]);

  const gated = status && !status.unlimited && !status.expired;

  // heartbeat: only while active, visible and not expired
  useEffect(() => {
    if (!gated) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      api.post(`/employee/playtime/ping?token=${token}`, { seconds: PING })
        .then((r) => { setStatus(r.data); setLocalRemaining(r.data.remaining_seconds); })
        .catch(() => {});
    }, PING * 1000);
    return () => clearInterval(id);
  }, [gated, token]);

  // local 1s countdown for a smooth timer display
  useEffect(() => {
    if (!gated) return;
    const id = setInterval(() => {
      setLocalRemaining((v) => {
        if (v === null) return v;
        const nv = v - 1;
        if (nv <= 0) {
          api.post(`/employee/playtime/ping?token=${token}`, { seconds: PING })
            .then((r) => setStatus(r.data)).catch(() => {});
          return 0;
        }
        return nv;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [gated, token]);

  if (status && status.expired) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--bq-bg)", color: "var(--bq-text)" }} data-testid="playtime-blocked">
        <div className="brutal-card p-8 md:p-12 max-w-lg w-full text-center" style={{ background: "var(--bq-card-bg)" }}>
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-4 themed-border" style={{ background: "var(--bq-danger)", color: "#fff" }}>
            <Lock size={40} />
          </div>
          <h1 className="text-3xl md:text-4xl font-black uppercase leading-none mb-3">Time's Up!</h1>
          <p className="text-base font-bold mb-2">You've used your play time for today.</p>
          <p className="text-sm mb-8" style={{ color: "var(--bq-muted)" }}>
            Come back tomorrow for another {status.limit_minutes} minute{status.limit_minutes === 1 ? "" : "s"} of fun. Your streak &amp; points are safe!
          </p>
          <button data-testid="blocked-back-hub" onClick={() => navigate("/play")} className="brutal-btn inline-flex items-center gap-2">
            <ArrowLeft size={16} /> Back to Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      {gated && localRemaining !== null && (
        <div
          data-testid="playtime-badge"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-2 border-2 border-black font-black uppercase text-sm"
          style={{ background: localRemaining <= 120 ? "var(--bq-danger)" : "var(--bq-accent)", color: localRemaining <= 120 ? "#fff" : "var(--bq-text)", boxShadow: "3px 3px 0 rgba(0,0,0,0.4)" }}
          title="Play time remaining today"
        >
          <Clock size={16} /> {fmt(localRemaining)}
        </div>
      )}
    </>
  );
}
